import React, { useState } from 'react';

import { Checkbox, Select, TextInput } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { LocalizedString, LocalizedTextInputs } from 'client/blocks/AliceAgentAppPage/LocalizedTextInputs';
import { Field } from 'client/blocks/form/Field/Field';
import { McpToolVisibility } from 'client/model/aliceAgentApp';
import * as keyset from 'i18n/AliceExternalAppForm';

import styles from './AliceExternalAppEditPage.module.scss';
import { ExternalFormState, isValidJsonOrEmpty } from './AliceExternalAppForm.types';
import { CardHeader } from './CardHeader';
import { withForm } from './formHook';
import { JsonEditor } from './JsonEditor';

const t = i18n(keyset);

const VISIBILITY_OPTIONS: { value: McpToolVisibility; labelKey: keyof typeof keyset.en }[] = [
    { value: 'public', labelKey: 'visibility-public' },
    { value: 'widget-only', labelKey: 'visibility-widget-only' },
    { value: 'hidden', labelKey: 'visibility-hidden' },
];

/**
 * Per-tool card: name/title/description/inputSchema/visibility/outputTemplate/invocation +
 * annotation checkboxes. The collapse state is local to each card.
 *
 * Built via TanStack-form's `withForm` — the parent passes a typed `form` reference instead of
 * us threading a hand-rolled `ReactFormExtendedApi<…, any × 11>` alias through props.
 */
export const ToolCard = withForm({
    defaultValues: {} as ExternalFormState,
    props: {
        index: 0,
        disabled: false,
        onRemove: () => {},
    },
    render: function ToolCardRender({ form, index, disabled, onRemove }) {
        const [expanded, setExpanded] = useState(true);
        const toggle = () => setExpanded((v) => !v);

        return (
            <div className={styles.card}>
                <CardHeader
                    expanded={expanded}
                    onToggle={toggle}
                    onRemove={onRemove}
                    removeDisabled={disabled}
                    title={
                        <form.Subscribe selector={(s) => s.values.tools[index]?.name ?? ''}>
                            {(name) => <span>{name || `tool[${index}]`}</span>}
                        </form.Subscribe>
                    }
                />
                {expanded && (
                    <div className={styles.cardBody}>
                        <form.Field name={`tools[${index}].name`}>
                            {(field) => (
                                <Field
                                    required
                                    fieldTitle={t('tool-field-name')}
                                    containerClassName={styles.compactField}
                                    size="m"
                                >
                                    <TextInput
                                        value={field.state.value}
                                        onUpdate={field.handleChange}
                                        disabled={disabled}
                                        size="m"
                                    />
                                </Field>
                            )}
                        </form.Field>

                        <form.Field name={`tools[${index}].title`}>
                            {(field) => (
                                <Field
                                    fieldTitle={t('tool-field-title')}
                                    containerClassName={styles.compactField}
                                    size="m"
                                >
                                    <LocalizedTextInputs
                                        value={field.state.value as LocalizedString}
                                        onUpdate={field.handleChange}
                                        disabled={disabled}
                                    />
                                </Field>
                            )}
                        </form.Field>

                        <form.Field name={`tools[${index}].description`}>
                            {(field) => (
                                <Field
                                    fieldTitle={t('tool-field-description')}
                                    containerClassName={styles.compactField}
                                    size="m"
                                >
                                    <LocalizedTextInputs
                                        value={field.state.value as LocalizedString}
                                        onUpdate={field.handleChange}
                                        disabled={disabled}
                                    />
                                </Field>
                            )}
                        </form.Field>

                        <form.Field
                            name={`tools[${index}].inputSchemaText`}
                            validators={{
                                onChange: ({ value }: { value: string }) =>
                                    isValidJsonOrEmpty(value) ? undefined : t('validation-invalid-json'),
                            }}
                        >
                            {(field) => (
                                <Field
                                    fieldTitle={t('tool-field-input-schema')}
                                    containerClassName={styles.compactField}
                                    size="m"
                                    error={field.state.meta.errors[0] as string | undefined}
                                >
                                    <JsonEditor
                                        value={field.state.value}
                                        onChange={field.handleChange}
                                        readOnly={disabled}
                                        error={Boolean(field.state.meta.errors[0])}
                                    />
                                </Field>
                            )}
                        </form.Field>

                        <form.Field name={`tools[${index}].visibility`}>
                            {(field) => (
                                <Field
                                    fieldTitle={t('tool-field-visibility')}
                                    containerClassName={styles.compactField}
                                    size="m"
                                >
                                    <Select
                                        value={[field.state.value]}
                                        onUpdate={(v) => field.handleChange(v[0] as McpToolVisibility)}
                                        disabled={disabled}
                                        size="m"
                                        options={VISIBILITY_OPTIONS.map((v) => ({
                                            value: v.value,
                                            content: t(v.labelKey),
                                        }))}
                                    />
                                </Field>
                            )}
                        </form.Field>

                        <form.Field name={`tools[${index}].outputTemplate`}>
                            {(field) => (
                                <Field
                                    fieldTitle={t('tool-field-output-template')}
                                    containerClassName={styles.compactField}
                                    size="m"
                                >
                                    <form.Subscribe selector={(s) => s.values.resources}>
                                        {(resources) => (
                                            <Select
                                                value={[field.state.value]}
                                                onUpdate={(v) => field.handleChange(v[0])}
                                                disabled={disabled}
                                                size="m"
                                                options={[
                                                    { value: '', content: t('tool-field-output-template-none') },
                                                    ...resources.map((r, ri) => ({
                                                        value: r.uri,
                                                        content: r.uri || `resource[${ri}]`,
                                                    })),
                                                ]}
                                            />
                                        )}
                                    </form.Subscribe>
                                </Field>
                            )}
                        </form.Field>

                        <form.Field name={`tools[${index}].invocation.invoking`}>
                            {(field) => (
                                <Field
                                    fieldTitle={t('tool-field-invocation-invoking')}
                                    containerClassName={styles.compactField}
                                    size="m"
                                >
                                    <LocalizedTextInputs
                                        value={field.state.value as LocalizedString}
                                        onUpdate={field.handleChange}
                                        disabled={disabled}
                                    />
                                </Field>
                            )}
                        </form.Field>

                        <form.Field name={`tools[${index}].invocation.invoked`}>
                            {(field) => (
                                <Field
                                    fieldTitle={t('tool-field-invocation-invoked')}
                                    containerClassName={styles.compactField}
                                    size="m"
                                >
                                    <LocalizedTextInputs
                                        value={field.state.value as LocalizedString}
                                        onUpdate={field.handleChange}
                                        disabled={disabled}
                                    />
                                </Field>
                            )}
                        </form.Field>

                        <div className={styles.cardAnnotations}>
                            <form.Field name={`tools[${index}].annotations.readOnlyHint`}>
                                {(field) => (
                                    <Checkbox
                                        checked={Boolean(field.state.value)}
                                        onChange={(e) => field.handleChange(e.target.checked)}
                                        disabled={disabled}
                                    >
                                        {t('tool-field-read-only-hint')}
                                    </Checkbox>
                                )}
                            </form.Field>
                            <form.Field name={`tools[${index}].annotations.destructiveHint`}>
                                {(field) => (
                                    <Checkbox
                                        checked={Boolean(field.state.value)}
                                        onChange={(e) => field.handleChange(e.target.checked)}
                                        disabled={disabled}
                                    >
                                        {t('tool-field-destructive-hint')}
                                    </Checkbox>
                                )}
                            </form.Field>
                            <form.Field name={`tools[${index}].annotations.idempotentHint`}>
                                {(field) => (
                                    <Checkbox
                                        checked={Boolean(field.state.value)}
                                        onChange={(e) => field.handleChange(e.target.checked)}
                                        disabled={disabled}
                                    >
                                        {t('tool-field-idempotent-hint')}
                                    </Checkbox>
                                )}
                            </form.Field>
                            <form.Field name={`tools[${index}].annotations.openWorldHint`}>
                                {(field) => (
                                    <Checkbox
                                        checked={Boolean(field.state.value)}
                                        onChange={(e) => field.handleChange(e.target.checked)}
                                        disabled={disabled}
                                    >
                                        {t('tool-field-open-world-hint')}
                                    </Checkbox>
                                )}
                            </form.Field>
                        </div>
                    </div>
                )}
            </div>
        );
    },
});
